import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import useMinLoad from "../hooks/useMinLoad";
import { authApi, billingApi, dashboardApi, importCandidatesApi, logout, sharingApi, supportApi, workspacesApi } from "../services/api";
import SavingNotice from "../components/shared/SavingNotice";
import ConfirmModal from "../components/shared/ConfirmModal";
import { useToast } from "../components/shared/Toast";
import useOnEscape from "../hooks/useOnEscape";
import { SHOPPING_REGION_OPTIONS } from "../utils/retailerLinks";

const CONNECTOR_META = {
  gmail:            { emoji: "✉️",  label: "Gmail",                 desc: "Scans receipts, bills, warranties & confirmations" },
  outlook:          { emoji: "📧",  label: "Outlook / Microsoft 365", desc: "Scans receipts, bills, warranties & confirmations" },
  google_calendar:  { emoji: "📅",  label: "Google Calendar",        desc: "Scans upcoming events, trips & appointments" },
  outlook_calendar: { emoji: "🗓️", label: "Outlook Calendar",       desc: "Scans upcoming events, trips & appointments" },
};

const PERMISSION_LABEL = {
  connected:          { text: "Connected",       color: "var(--teal, #2A7B6F)" },
  not_connected:      { text: "Not connected",   color: "var(--ink-hint)" },
  pending_user_setup: { text: "Setup pending",   color: "var(--amber, #D97706)" },
  token_exchange_failed: { text: "Auth failed",  color: "var(--rose, #E7614A)" },
};

function DiscoveryConnectors({ sources = [], onConnect, onDisconnect }) {
  const oauthSources = sources.filter(s =>
    s.source_type === "oauth" || s.source_type === "calendar_oauth"
  );
  if (oauthSources.length === 0) {
    return (
      <p style={{ fontSize: 13, color: "var(--ink-hint)", margin: "8px 0 0" }}>
        Loading connectors…
      </p>
    );
  }
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
      {oauthSources.map(source => {
        const meta   = CONNECTOR_META[source.provider] || {};
        const pMeta  = PERMISSION_LABEL[source.permission_status] || PERMISSION_LABEL.not_connected;
        const isConnected = source.permission_status === "connected";
        const isFailed    = source.permission_status === "token_exchange_failed";
        return (
          <div key={source.id || source.provider} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 14px",
            border: `1px solid ${isConnected ? "var(--teal, #2A7B6F)" : "var(--border)"}`,
            borderRadius: "var(--radius-sm, 8px)",
            background: isConnected ? "rgba(42,123,111,0.05)" : "transparent",
          }}>
            <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{meta.emoji || "🔌"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
                {meta.label || source.label}
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-hint)" }}>{meta.desc || source.access_level}</div>
              {source.source_account && (
                <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 2 }}>
                  {source.source_account}
                </div>
              )}
              {source.last_synced_at && (
                <div style={{ fontSize: 11, color: "var(--ink-hint)", marginTop: 2 }}>
                  Last synced: {new Date(source.last_synced_at).toLocaleDateString()}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: pMeta.color }}>
                {pMeta.text}
              </span>
              {isConnected ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ fontSize: 12 }}
                  onClick={() => onDisconnect(source)}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ fontSize: 12 }}
                  onClick={() => onConnect(source)}
                >
                  {isFailed ? "Retry" : "Connect"}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const SUPPORT_EMAIL = "support@housemindos.com";
const SITE_URL = "https://housemindos.com";
const BILLING_TARGET_KEY = "housemind_billing_target_plan";

const AI_OPTIONS = [
  { value: "openai",    label: "OpenAI (gpt-4o-mini)",   sub: "Default — fast, balanced suggestions for everyday planning" },
  { value: "anthropic", label: "Claude (Anthropic)", sub: "Premium feel — warmer, more nuanced suggestions" },
];

const THEME_OPTIONS = [
  { value: "light", label: "Light", sub: "Soft, bright workspace for daytime planning" },
  { value: "dark", label: "Dark", sub: "Low-glare view for evening sessions and shared screens" },
  { value: "system", label: "System", sub: "Match the theme your device is already using" },
];

const REMINDER_OPTIONS = [
  { value: 21, label: "3 weeks before" },
  { value: 14, label: "2 weeks before" },
  { value: 7,  label: "1 week before" },
  { value: 3,  label: "3 days before" },
  { value: 1,  label: "1 day before" },
];

const SUPPORT_CATEGORIES = [
  { value: "billing", label: "Billing" },
  { value: "login", label: "Login help" },
  { value: "bug", label: "Bug report" },
  { value: "feature", label: "Feature request" },
  { value: "household", label: "Gift, record, trip, or household issue" },
  { value: "other", label: "Other" },
];

const ACCOUNT_AVATAR_OPTIONS = [
  { label: "Star", emoji: "⭐" },
  { label: "Sparkles", emoji: "✨" },
  { label: "Heart", emoji: "💖" },
  { label: "Gift", emoji: "🎁" },
  { label: "Balloon", emoji: "🎈" },
  { label: "Crown", emoji: "👑" },
  { label: "Flower", emoji: "🌸" },
  { label: "Teddy", emoji: "🧸" },
];

function formatBillingDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function AccountAvatarField({ fullName, value, onChange, onBusyChange, onActionChange }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(value || "");
  const [selectedPreset, setSelectedPreset] = useState("");
  const [avatarError, setAvatarError] = useState(null);
  const initials = (fullName || "?").split(" ").map(word => word[0]).join("").slice(0, 2).toUpperCase();
  const isEmojiAvatar = preview && !String(preview).startsWith("data:image/");

  useEffect(() => {
    setPreview(value || "");
    const matchingPreset = ACCOUNT_AVATAR_OPTIONS.find(option => option.emoji === value);
    setSelectedPreset(matchingPreset?.label || "");
  }, [value]);

  const pulseBusy = async (action, work) => {
    onActionChange?.(action);
    onBusyChange?.(true);
    try {
      await work();
      await new Promise(resolve => setTimeout(resolve, 180));
    } finally {
      onBusyChange?.(false);
      onActionChange?.("");
    }
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const image = new Image();
      image.onload = () => {
        const maxDimension = 512;
        const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("Could not process this image."));
          return;
        }
        context.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      image.onerror = () => reject(new Error("Could not read this image."));
      image.src = reader.result;
    };
    reader.onerror = () => reject(new Error("Could not load this file."));
    reader.readAsDataURL(file);
  });

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAvatarError(null);
    onActionChange?.("save");
    onBusyChange?.(true);
    try {
      const compressed = await compressImage(file);
      setSelectedPreset("");
      setPreview(compressed);
      onChange(compressed);
    } catch (error) {
      setAvatarError(error.message || "Could not prepare that image.");
    } finally {
      onBusyChange?.(false);
      onActionChange?.("");
      event.target.value = "";
    }
  };

  return (
    <div className="form-group">
      <label className="form-label">Account avatar</label>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            border: "2px dashed var(--border-strong)",
            background: "var(--rose-light)",
            color: "var(--rose)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            cursor: "pointer",
            fontSize: 24,
            flexShrink: 0,
          }}
        >
          {preview ? (
            isEmojiAvatar
              ? <span>{preview}</span>
              : <img src={preview} alt="Account avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : initials}
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="flex-center gap-8" style={{ justifyContent: "flex-start", flexWrap: "wrap", marginBottom: 8 }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => fileRef.current?.click()}>
              Upload photo
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => pulseBusy("remove", async () => {
                setSelectedPreset("");
                setPreview("");
                onChange("");
              })}
            >
              Remove
            </button>
          </div>
          <select
            className="form-select"
            value={selectedPreset}
            onChange={(e) => {
              pulseBusy("save", async () => {
                const option = ACCOUNT_AVATAR_OPTIONS.find(item => item.label === e.target.value);
                setSelectedPreset(option?.label || "");
                setPreview(option?.emoji || "");
                onChange(option?.emoji || "");
              });
            }}
          >
            <option value="">Choose an emoji avatar</option>
            {ACCOUNT_AVATAR_OPTIONS.map(option => (
              <option key={option.label} value={option.label}>{option.emoji} {option.label}</option>
            ))}
          </select>
          <div className="text-sm text-muted mt-8">
            This avatar appears in the sidebar and account identity areas.
          </div>
          {avatarError && <div className="text-sm mt-8" style={{ color: "var(--rose-dark)" }}>{avatarError}</div>}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

export default function SettingsPage() {
  const {
    user,
    updateUser,
    refreshUser,
    themePreference,
    setThemePreference,
    shoppingRegion,
    setShoppingRegion,
    accountAvatar,
    setAccountAvatar,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    avatar_url: accountAvatar || user?.avatar_url || "",
    ai_provider: user?.ai_provider || "openai",
    reminder_days_before: user?.reminder_days_before ?? 21,
    theme_preference: themePreference || "system",
    shopping_region: shoppingRegion || "",
  });
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [avatarSaving, setAvatarSaving] = useState(false);
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const [regionSaving, setRegionSaving] = useState(false);
  const [intelligenceRunning, setIntelligenceRunning] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState(null);
  const showOverlay = (msg, duration = 900) => {
    setOverlayMessage(msg);
    setTimeout(() => setOverlayMessage(null), duration);
  };
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [avatarPreparing, setAvatarPreparing] = useState(false);
  const [avatarAction, setAvatarAction] = useState("");
  const [workspaceActionMessage, setWorkspaceActionMessage] = useState("");
  const [settingsNoticeMessage, setSettingsNoticeMessage] = useState("");
  const [settingsNoticeTitle, setSettingsNoticeTitle] = useState("Saving");
  const [error, setError] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingLoadingTarget, setBillingLoadingTarget] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingPlans, setBillingPlans] = useState([]);
  const [billingNotice, setBillingNotice] = useState(null);
  const [supportForm, setSupportForm] = useState({
    category: "other",
    subject: "",
    message: "",
  });
  const [supportSending, setSupportSending] = useState(false);
  const [supportNotice, setSupportNotice] = useState(null);
  const [workspaceState, setWorkspaceState] = useState(null);
  const [workspaceLoading, setWorkspaceLoading] = useState(false);
  const [workspaceNotice, setWorkspaceNotice] = useState(null);
  const [discoverySourcesPayload, setDiscoverySourcesPayload] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useOnEscape(useCallback(() => {
    if (confirmAction) { setConfirmAction(null); return; }
    if (billingNotice) setBillingNotice(null);
  }, [confirmAction, billingNotice]));

  useEffect(() => {
    if (!location.hash) return undefined;
    const sectionId = location.hash.slice(1);
    const timeout = setTimeout(() => {
      const target = document.getElementById(sectionId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 80);
    return () => clearTimeout(timeout);
  }, [location.hash]);
  const used = user?.ai_requests_used ?? 0;
  const limit = user?.ai_requests_limit ?? 0;
  const remaining = Math.max(limit - used, 0);
  const activeWorkspacePlan = workspaceState?.workspace?.plan || user?.workspace?.plan || user?.plan || "free";
  const subscribedPlan = billingStatus?.plan || user?.plan || "free";
  const billingWorkspace = billingStatus?.billing_workspace || workspaceState?.workspace || user?.workspace;
  const familyWorkspace = workspaceState?.family_workspace || billingStatus?.billing_workspace || null;
  const billingPeriodStartLabel = formatBillingDate(billingStatus?.current_period_start);
  const billingPeriodEndLabel = formatBillingDate(billingStatus?.current_period_end);
  const billingAccessExpiresAt = billingStatus?.access_expires_at || billingWorkspace?.access_expires_at || null;
  const scheduledPlanEndLabel = formatBillingDate(billingAccessExpiresAt);
  const hasScheduledPlanEnd = Boolean(
    billingStatus?.stripe_subscription_id
    && billingStatus?.cancel_at_period_end
    && subscribedPlan !== "free"
  );
  const hasActivePaidSubscription = Boolean(billingStatus?.stripe_subscription_id && subscribedPlan !== "free");
  const currentWorkspaceType = workspaceState?.workspace?.workspace_type || user?.workspace?.workspace_type || "personal";
  const currentWorkspaceRole = workspaceState?.role || "owner";
  const isStaleSharedWorkspaceOwner = currentWorkspaceType === "shared"
    && currentWorkspaceRole === "owner"
    && activeWorkspacePlan !== "family";
  const canManageFamilyMembers = Boolean(workspaceState?.can_manage_members && activeWorkspacePlan === "family");
  const duplicatePeopleCleanup = workspaceState?.duplicate_people_cleanup;
  const discoverySources = discoverySourcesPayload?.sources || [];
  const activeSettingsNoticeMessage = settingsLoading
    ? "Loading settings..."
    : avatarPreparing
      ? avatarAction === "remove"
        ? "Removing your profile photo..."
        : "Updating your avatar preview..."
      : avatarSaving
        ? avatarAction === "remove"
          ? "Removing your profile photo..."
          : "Saving your profile photo..."
        : workspaceActionMessage
          ? workspaceActionMessage
        : regionSaving
          ? "Saving shopping region..."
          : supportSending
            ? "Sending support message..."
          : intelligenceRunning
            ? "Refreshing household intelligence..."
          : preferenceSaving
            ? "Saving preferences..."
            : saving
              ? "Saving account..."
              : "";
  const activeSettingsNoticeTitle = settingsLoading
    ? "Loading"
    : avatarPreparing
      ? avatarAction === "remove"
        ? "Removing"
        : "Updating"
      : avatarSaving
        ? avatarAction === "remove"
          ? "Removing"
          : "Saving"
      : workspaceActionMessage
        ? workspaceActionMessage.startsWith("Adding")
          ? "Adding"
          : workspaceActionMessage.startsWith("Switching")
            ? "Switching"
            : workspaceActionMessage.startsWith("Leaving")
              ? "Leaving"
              : workspaceActionMessage.startsWith("Updating")
                ? "Updating"
                : workspaceActionMessage.startsWith("Removing")
                  ? "Removing"
                  : workspaceActionMessage.startsWith("Repairing")
                    ? "Repairing"
                    : "Working"
        : regionSaving
          ? "Saving"
        : supportSending
          ? "Sending"
          : intelligenceRunning
            ? "Scanning"
          : "Saving";
  const showSettingsNotice = useMinLoad(Boolean(activeSettingsNoticeMessage), 700);
  useEffect(() => {
    const loadInitial = async () => {
      setSettingsLoading(true);
      try {
        const [statusRes, plansRes, workspaceRes, discoveryRes] = await Promise.allSettled([
          billingApi.status(),
          billingApi.plans(),
          workspacesApi.current(),
          importCandidatesApi.sources(),
        ]);
        if (statusRes.status === "fulfilled") setBillingStatus(statusRes.value);
        if (plansRes.status === "fulfilled") setBillingPlans(plansRes.value.plans || []);
        if (workspaceRes.status === "fulfilled") setWorkspaceState(workspaceRes.value);
        if (discoveryRes.status === "fulfilled") setDiscoverySourcesPayload(discoveryRes.value);
      } finally {
        setSettingsLoading(false);
      }
    };

    loadInitial();
  }, []);

  useEffect(() => {
    setForm(current => ({ ...current, theme_preference: themePreference || "system" }));
  }, [themePreference]);

  useEffect(() => {
    setForm(current => ({ ...current, shopping_region: shoppingRegion || "" }));
  }, [shoppingRegion]);

  useEffect(() => {
    setForm(current => ({
      ...current,
      full_name: user?.full_name || "",
      avatar_url: accountAvatar || user?.avatar_url || "",
      ai_provider: user?.ai_provider || "openai",
      reminder_days_before: user?.reminder_days_before ?? 21,
    }));
  }, [accountAvatar, user?.full_name, user?.avatar_url, user?.ai_provider, user?.reminder_days_before]);

  useEffect(() => {
    const billingState = searchParams.get("billing");
    if (!billingState) return;

    if (billingState === "success") {
      const targetPlan = sessionStorage.getItem(BILLING_TARGET_KEY);
      setBillingNotice({
        tone: "success",
        message: "Payment completed. We’re confirming your updated plan now.",
      });

      const syncBillingState = async () => {
        const attempts = targetPlan ? 6 : 2;
        for (let attempt = 0; attempt < attempts; attempt += 1) {
          const [, statusRes, workspaceRes] = await Promise.allSettled([
            refreshUser(),
            billingApi.status(),
            workspacesApi.current(),
          ]);

          if (statusRes.status === "fulfilled") setBillingStatus(statusRes.value);
          if (workspaceRes.status === "fulfilled") setWorkspaceState(workspaceRes.value);

          const syncedPlan =
            statusRes.status === "fulfilled"
              ? (statusRes.value.workspace?.plan || statusRes.value.plan)
              : null;

          if (!targetPlan || (syncedPlan && syncedPlan === targetPlan)) {
            sessionStorage.removeItem(BILLING_TARGET_KEY);
            setBillingNotice({
              tone: "success",
              message: "Your subscription details were updated successfully.",
            });
            return;
          }

          if (attempt < attempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 2500));
          }
        }

        if (targetPlan) {
          setBillingNotice({
            tone: "warn",
            message: `Payment completed, but your ${targetPlan} plan is still syncing. If it stays unchanged, the Stripe webhook likely still needs attention.`,
          });
        } else {
          setBillingNotice({
            tone: "warn",
            message: "Checkout finished, but billing details may still be updating. Refresh this page in a few seconds.",
          });
        }
      };

      syncBillingState();
    } else if (billingState === "cancel") {
      sessionStorage.removeItem(BILLING_TARGET_KEY);
      setBillingNotice({
        tone: "warn",
        message: "Checkout was canceled. Your current plan has not changed.",
      });
    }

    const next = new URLSearchParams(searchParams);
    next.delete("billing");
    setSearchParams(next, { replace: true });
  }, [refreshUser, searchParams, setSearchParams]);

  useEffect(() => {
    if (activeSettingsNoticeMessage) {
      setSettingsNoticeMessage(activeSettingsNoticeMessage);
      setSettingsNoticeTitle(activeSettingsNoticeTitle);
    }
  }, [activeSettingsNoticeMessage, activeSettingsNoticeTitle]);

  // Reset billing loading state when the page is restored from bfcache (back-forward cache).
  // Mobile browsers (Android/iOS) preserve React state on back-navigation, so if the user
  // navigated to Stripe portal/checkout and pressed Back, loading would be stuck forever.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        setBillingLoading(false);
        setBillingLoadingTarget(null);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    if (!workspaceNotice) return undefined;
    const timeoutMs = workspaceNotice.tone === "error" ? 6000 : 4000;
    const timer = setTimeout(() => {
      setWorkspaceNotice((current) => (current === workspaceNotice ? null : current));
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [workspaceNotice]);

  useEffect(() => {
    if (!supportNotice) return undefined;
    const timeoutMs = supportNotice.tone === "error" ? 7000 : 4500;
    const timer = setTimeout(() => {
      setSupportNotice((current) => (current === supportNotice ? null : current));
    }, timeoutMs);
    return () => clearTimeout(timer);
  }, [supportNotice]);

  const handleAccountSave = async () => {
    if (!form.full_name.trim()) {
      setError("Full name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateUser({
        full_name: form.full_name,
      });
      toast("Name saved", "success");
    } catch (err) {
      setError(err.message || "Failed to save name");
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (avatarUrl) => {
    const previousAvatar = form.avatar_url || "";
    setAvatarAction(avatarUrl ? "save" : "remove");
    setForm(current => ({ ...current, avatar_url: avatarUrl }));
    setAccountAvatar(avatarUrl);
    setAvatarSaving(true);
    setError(null);
    try {
      await updateUser({ avatar_url: avatarUrl });
      toast(avatarUrl ? "Profile photo saved" : "Profile photo removed", "success");
    } catch (err) {
      setForm(current => ({ ...current, avatar_url: previousAvatar }));
      setAccountAvatar(previousAvatar);
      setError(err.message || "Failed to save profile photo");
    } finally {
      setAvatarSaving(false);
      setAvatarAction("");
    }
  };

  const handlePreferenceSave = async () => {
    setPreferenceSaving(true);
    setError(null);
    try {
      await updateUser({
        ai_provider: form.ai_provider,
        reminder_days_before: form.reminder_days_before,
      });
      setThemePreference(form.theme_preference);
      toast("Preferences saved", "success");
    } catch (err) {
      setError(err.message || "Failed to save preferences");
    } finally {
      setPreferenceSaving(false);
    }
  };

  const handleProviderChange = async (provider) => {
    const previousProvider = form.ai_provider;
    setForm(current => ({ ...current, ai_provider: provider }));
    showOverlay("Applying AI provider...");
    setPreferenceSaving(true);
    setError(null);
    try {
      await updateUser({ ai_provider: provider });
      toast("AI provider updated", "success");
    } catch (err) {
      setForm(current => ({ ...current, ai_provider: previousProvider }));
      setError(err.message || "Failed to save AI provider");
    } finally {
      setPreferenceSaving(false);
    }
  };


  const handleRefreshIntelligence = async () => {
    setIntelligenceRunning(true);
    setError(null);
    try {
      await dashboardApi.runEngine();
      const insights = await dashboardApi.insights();
      const visibleInsights = Array.isArray(insights) ? insights : [];
      if (visibleInsights.length > 0) {
        toast("Household intelligence refreshed", "success");
        navigate("/app?intelligence=refreshed");
      } else {
        toast("Household intelligence refreshed. No new cross-module insights right now.", "info");
      }
    } catch (err) {
      const message = err.message || "Could not refresh household intelligence right now.";
      setError(message);
      toast(message, "error");
    } finally {
      setIntelligenceRunning(false);
    }
  };

  const handleShoppingRegionChange = async (value) => {
    setForm(current => ({ ...current, shopping_region: value }));
    setRegionSaving(true);
    setError(null);
    const startedAt = Date.now();
    try {
      setShoppingRegion(value);
      const elapsed = Date.now() - startedAt;
      if (elapsed < 450) {
        await new Promise(resolve => setTimeout(resolve, 450 - elapsed));
      }
      toast("Shopping region saved", "success");
    } catch (err) {
      setError(err.message || "Failed to save shopping region");
    } finally {
      setRegionSaving(false);
    }
  };

  const getFamilyPlanEndDetails = async () => {
    const memberNames = (workspaceState?.members || [])
      .filter(member => member.role !== "owner")
      .map(member => member.full_name || member.email)
      .filter(Boolean);
    let sharedCount = 0;
    try {
      const summary = await sharingApi.summary();
      sharedCount = (summary?.by_me || []).length + (summary?.with_me || []).length;
    } catch {
      sharedCount = 0;
    }
    return { memberNames, sharedCount };
  };

  const familyEndWarning = ({ memberNames, sharedCount }) => {
    const memberLine = memberNames.length
      ? `${memberNames.join(", ")} ${memberNames.length === 1 ? "will be moved" : "will be moved"} back to their own Free account.`
      : "Family member seats will end.";
    const shareLine = sharedCount
      ? `${sharedCount} shared ${sharedCount === 1 ? "item" : "items"} will stop being shared.`
      : "All shared items will become private again.";
    return `${memberLine} ${shareLine} Your data and their data stay safe. Nothing is deleted; sharing simply stops.`;
  };

  const handlePlanSelect = async (plan) => {
    if (subscribedPlan === "family" && plan === "pro") {
      const details = await getFamilyPlanEndDetails();
      setConfirmAction({
        title: "Downgrade Family plan",
        message: "You're about to change from Family to Pro.",
        warning: familyEndWarning(details),
        confirmLabel: billingStatus?.stripe_customer_id ? "Continue in Stripe" : "Confirm downgrade",
        destructive: true,
        onConfirm: billingStatus?.stripe_customer_id ? handleManageBilling : () => handleUpgrade(plan),
      });
      return;
    }
    handleUpgrade(plan);
  };

  const handleUpgrade = async (plan) => {
    setBillingLoading(true);
    setBillingLoadingTarget(plan);
    setError(null);
    try {
      sessionStorage.setItem(BILLING_TARGET_KEY, plan);
      const data = await billingApi.createCheckoutSession(plan);
      window.location.href = data.checkout_url;
    } catch (err) {
      sessionStorage.removeItem(BILLING_TARGET_KEY);
      setError(err.message || "Unable to start checkout");
      setBillingLoading(false);
      setBillingLoadingTarget(null);
    }
  };

  const handleManageBilling = async () => {
    setBillingLoading(true);
    setBillingLoadingTarget("portal");
    setError(null);
    try {
      const data = await billingApi.createPortalSession();
      window.location.href = data.portal_url;
    } catch (err) {
      setError(err.message || "Unable to open billing portal");
      setBillingLoading(false);
      setBillingLoadingTarget(null);
    }
  };

  const handleCancelSubscription = async () => {
    setBillingLoading(true);
    setBillingLoadingTarget("cancel");
    setError(null);
    try {
      const [cancelRes, workspaceRes] = await Promise.all([
        billingApi.cancelSubscription(),
        refreshUser(),
        workspacesApi.current(),
      ]);
      setBillingStatus(cancelRes);
      setWorkspaceState(workspaceRes);
      sessionStorage.removeItem(BILLING_TARGET_KEY);
      setBillingNotice({
        tone: "success",
        message: cancelRes.access_expires_at
          ? `Your subscription will stay active until ${formatBillingDate(cancelRes.access_expires_at)}. It will switch to Free when this Stripe billing period ends.`
          : "Your subscription has been updated.",
      });
    } catch (err) {
      setError(err.message || "Unable to cancel the subscription right now.");
    } finally {
      setBillingLoading(false);
      setBillingLoadingTarget(null);
    }
  };

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setSupportSending(true);
    setSupportNotice(null);
    try {
      const res = await supportApi.contact({
        full_name: user?.full_name || form.full_name,
        email: user?.email || "",
        category: supportForm.category,
        subject: supportForm.subject,
        message: supportForm.message,
      });
      setSupportNotice({
        tone: "success",
        message: res.auto_reply_sent
          ? `Message sent. A confirmation email was sent to ${res.recipient_email || user?.email || "your account email"} from ${res.support_email}.`
          : `Message sent to ${res.support_email}.`,
      });
      setSupportForm({ category: "other", subject: "", message: "" });
    } catch (err) {
      setSupportNotice({
        tone: "error",
        message: err.message || `Support request failed. Please email ${SUPPORT_EMAIL} directly.`,
      });
    } finally {
      setSupportSending(false);
    }
  };

  const handleLeaveWorkspace = async () => {
    setWorkspaceActionMessage("Leaving workspace...");
    setWorkspaceLoading(true);
    setWorkspaceNotice(null);
    try {
      await workspacesApi.leave();
      const [currentWorkspace] = await Promise.all([
        workspacesApi.current(),
        refreshUser(),
      ]);
      setWorkspaceState(currentWorkspace);
      setWorkspaceNotice({
        tone: "success",
        message: "You left the Family plan and kept your private account.",
      });
    } catch (err) {
      setWorkspaceNotice({
        tone: "error",
        message: err.message || "Unable to leave this workspace right now.",
      });
    } finally {
      setWorkspaceLoading(false);
      setWorkspaceActionMessage("");
    }
  };

  const handleRoleChange = async (membershipId, role) => {
    setWorkspaceActionMessage("Updating member role...");
    setWorkspaceLoading(true);
    setWorkspaceNotice(null);
    try {
      const res = await workspacesApi.updateMemberRole(membershipId, role);
      setWorkspaceState(current => current ? { ...current, members: res.members, workspace: res.workspace } : current);
      setWorkspaceNotice({
        tone: "success",
        message: "Member role updated.",
      });
    } catch (err) {
      setWorkspaceNotice({
        tone: "error",
        message: err.message || "Unable to update that role right now.",
      });
    } finally {
      setWorkspaceLoading(false);
      setWorkspaceActionMessage("");
    }
  };

  const performRemoveMember = async (membershipId) => {
    setWorkspaceActionMessage("Removing member...");
    setWorkspaceLoading(true);
    setWorkspaceNotice(null);
    const member = (workspaceState?.members || []).find(item => item.membership_id === membershipId);
    try {
      const res = await workspacesApi.removeMember(membershipId);
      setWorkspaceState(current => current ? { ...current, members: res.members } : current);
      await refreshUser();
      toast(`${member?.email || "That member"} was removed from this Family plan.`, "success");
      setWorkspaceNotice({
        tone: "success",
        message: res.shares_revoked
          ? `Member removed. ${res.shares_revoked} shared ${res.shares_revoked === 1 ? "item was" : "items were"} made private again.`
          : "Member removed from the Family plan.",
      });
    } catch (err) {
      setWorkspaceNotice({
        tone: "error",
        message: err.message || "Unable to remove that member right now.",
      });
    } finally {
      setWorkspaceLoading(false);
      setWorkspaceActionMessage("");
    }
  };

  const handleRepairDuplicatePeople = async () => {
    setWorkspaceActionMessage("Repairing duplicates...");
    setWorkspaceLoading(true);
    setWorkspaceNotice(null);
    try {
      const res = await workspacesApi.repairDuplicatePeople();
      setWorkspaceState(current => current ? {
        ...current,
        workspace: res.workspace,
        members: res.members,
        duplicate_people_cleanup: res.duplicate_people_cleanup,
      } : current);
      toast(res.message, "success");
      setWorkspaceNotice({ tone: "success", message: res.message });
      await refreshUser();
    } catch (err) {
      setWorkspaceNotice({
        tone: "error",
        message: err.message || "Unable to repair duplicate people right now.",
      });
    } finally {
      setWorkspaceLoading(false);
      setWorkspaceActionMessage("");
    }
  };

  const handleDeleteAccount = async () => {
    setSaving(true);
    setError(null);
    try {
      await authApi.deleteMe();
      logout();
      window.location.href = "/login";
    } catch (err) {
      setError(err.message || "Unable to delete your account right now.");
      setSaving(false);
    }
  };

  return (
    <div className="settings-page-shell">
      {overlayMessage && createPortal(
        <div style={{
          position: "fixed", inset: 0, zIndex: 1200,
          background: "rgba(28,25,23,0.45)",
          backdropFilter: "blur(3px)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "var(--warm-white)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border)",
            padding: "28px 36px",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
            boxShadow: "0 8px 32px rgba(28,25,23,0.18)",
          }}>
            <span className="spinner" style={{
              width: 28, height: 28, borderWidth: 3,
              borderColor: "var(--border-strong)",
              borderTopColor: "var(--rose)",
            }} />
            <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 300, color: "var(--ink)" }}>
              {overlayMessage}
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
              Just a moment
            </div>
          </div>
        </div>,
        document.body
      )}
      {billingNotice && (
        <div className="workspace-toast-backdrop" onClick={() => setBillingNotice(null)}>
          <div className="card modal-card" style={{ maxWidth: 560, width: "calc(100% - 32px)" }} onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow-label" style={{ marginBottom: 8, color: billingNotice.tone === "success" ? "var(--green)" : "var(--gold-dark)" }}>
              {billingNotice.tone === "success" ? "Payment completed" : "Billing notice"}
            </div>
            <h3 style={{ fontSize: 28, marginBottom: 10 }}>
              {billingNotice.tone === "success" ? "Your subscription is active" : "Your billing needs attention"}
            </h3>
            <p className="text-muted" style={{ marginBottom: 18 }}>
              {billingNotice.message}
            </p>
            <div className={`notice-banner ${billingNotice.tone === "success" ? "is-success" : "is-warn"} mb-16`}>
              <p>
                {billingNotice.tone === "success"
                  ? "You can keep planning right away, and your updated limits or family account features should now be available."
                  : "If the status still looks unchanged after a few seconds, refresh this page once and check again."}
              </p>
            </div>
            <div className="flex-between" style={{ justifyContent: "flex-end", gap: 10 }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setBillingNotice(null)}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          tag={confirmAction.destructive ? "⚠ Confirm action" : "Confirm action"}
          title={confirmAction.title}
          message={[confirmAction.message, confirmAction.warning].filter(Boolean).join(" ")}
          confirmLabel={confirmAction.confirmLabel || "Confirm"}
          danger={Boolean(confirmAction.destructive)}
          onCancel={() => setConfirmAction(null)}
          onConfirm={async () => {
            const action = confirmAction.onConfirm;
            setConfirmAction(null);
            await action();
          }}
        />
      )}

      <div className="page-header">
        <h2>Settings</h2>
        <p>Manage your account, billing, family seats, and the models powering your planning tools.</p>
      </div>

      {error && (
        <div className="notice-banner is-error mb-16">
          <p>{error}</p>
        </div>
      )}
      <div>
        {/* Account */}
        <div className="card mb-16">
          <h3 className="section-card-title">👤 Account</h3>
          <p className="section-card-copy">Keep your basic profile details current so reminders and support messages reach the right person.</p>
          <AccountAvatarField
            fullName={form.full_name}
            value={form.avatar_url}
            onChange={handleAvatarChange}
            onBusyChange={setAvatarPreparing}
            onActionChange={setAvatarAction}
          />
          <div className="form-group">
            <label className="form-label">Full name</label>
            <input className="form-input" value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              required />
          </div>
          <div style={{ marginTop: 16, marginBottom: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
            <div style={{ fontSize: 13, color: "var(--ink-hint)", marginBottom: 10 }}>
              Photo changes save automatically. Use this button only when you change your name.
            </div>
            <button type="button" className="btn btn-primary" disabled={saving || avatarPreparing || avatarSaving} onClick={handleAccountSave}>
              {saving ? <><span className="spinner" /> Saving...</> : "Save name"}
            </button>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Email</label>
            <input className="form-input" value={user?.email || ""} disabled
              style={{ opacity: 0.6, cursor: "not-allowed" }} />
            <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 8 }}>
              {user?.email_verified
                ? "Email status: verified"
                : "Email status: unverified. New accounts can sign in right away, but we'll treat this address as unverified until it is confirmed."}
            </div>
          </div>
        </div>

        <div className="card mb-16">
          <h3 className="section-card-title">💳 Billing</h3>
          <p className="section-card-copy">Choose the plan that fits your household planning, people, gifts, records, and coordination needs.</p>
          <div style={{ display: "grid", gap: 12 }}>
            {billingPlans.map(plan => {
              const isCurrent = subscribedPlan === plan.id;
              const selectionLockedByScheduledEnd = hasScheduledPlanEnd && !isCurrent;
              return (
                <div key={plan.id} style={{
                  padding: "14px 16px",
                  border: "1px solid",
                  borderColor: isCurrent ? "var(--rose)" : "var(--border)",
                  background: isCurrent ? "var(--rose-light)" : "var(--warm-white)",
                  borderRadius: "var(--radius-sm)",
                }}>
                  <div className="flex-between settings-plan-row" style={{ gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 600 }}>
                        {plan.name} {plan.price > 0 ? `- $${plan.price}/mo` : "- $0"}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>
                        {plan.id === "free" && "10 AI requests per month, with people and household records still included"}
                        {plan.id === "pro" && "100 AI requests per month"}
                        {plan.id === "family" && "3 private accounts and one shared 300-request Family AI pool"}
                      </div>
                    </div>
            {plan.id !== "free" && !isCurrent && (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={
                          billingLoading
                          || selectionLockedByScheduledEnd
                          || (subscribedPlan === "family" && !billingStatus?.can_manage_billing)
                        }
                        onClick={() => handlePlanSelect(plan.id)}
                      >
                        {billingLoading && billingLoadingTarget === plan.id
                          ? <><span className="spinner" style={{ borderColor: "rgba(28,25,23,0.15)", borderTopColor: "var(--ink)" }} /> Loading…</>
                          : `Choose ${plan.name}`}
                      </button>
                    )}
                    {isCurrent && (
                      <span className="badge" style={{ background: "var(--warm-white)", color: "var(--rose)" }}>
                        Current plan
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex-between mt-16" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
              Subscription status: <strong style={{ textTransform: "capitalize" }}>{billingStatus?.stripe_subscription_status || "free"}</strong>
            </div>
            <div className="flex-center gap-8" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
              {billingStatus?.stripe_customer_id && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={billingLoading || !billingStatus?.can_manage_billing}
                  onClick={handleManageBilling}
                >
                  {billingLoading && billingLoadingTarget === "portal"
                    ? <><span className="spinner" style={{ borderColor: "rgba(28,25,23,0.15)", borderTopColor: "var(--ink)" }} /> Loading…</>
                    : "Manage billing"}
                </button>
              )}
              {billingStatus?.stripe_subscription_id && subscribedPlan !== "free" && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ color: "var(--rose)" }}
                  disabled={billingLoading || !billingStatus?.can_manage_billing || hasScheduledPlanEnd}
                  onClick={async () => {
                    const familyDetails = subscribedPlan === "family" ? await getFamilyPlanEndDetails() : null;
                    setConfirmAction({
                      title: "Cancel subscription",
                      message: "Cancel this Stripe subscription at the end of the current billing period?",
                      warning: subscribedPlan === "family"
                        ? `When the Family plan ends, ${familyEndWarning(familyDetails)}`
                        : "Your paid plan benefits stay active through the paid billing period, then switch to Free at period end.",
                      confirmLabel: "Cancel subscription",
                      destructive: true,
                      onConfirm: handleCancelSubscription,
                    });
                  }}
                >
                  {billingLoading && billingLoadingTarget === "cancel"
                    ? <><span className="spinner" style={{ borderColor: "rgba(231,97,74,0.2)", borderTopColor: "var(--rose)" }} /> Canceling…</>
                    : hasScheduledPlanEnd ? "Cancellation scheduled" : "Cancel subscription"}
                </button>
              )}
            </div>
          </div>
          {subscribedPlan !== "free" && (
            <div className="soft-info-panel mt-16">
              <div className="soft-info-title">Billing cycle</div>
              <div className="soft-info-copy">
                {billingPeriodStartLabel && billingPeriodEndLabel
                  ? <>Current billing period: <strong>{billingPeriodStartLabel}</strong> to <strong>{billingPeriodEndLabel}</strong>.</>
                  : billingPeriodEndLabel
                    ? <>Current billing period ends on <strong>{billingPeriodEndLabel}</strong>.</>
                    : "Billing dates are syncing from Stripe."}
              </div>
              {hasScheduledPlanEnd ? (
                <div className="soft-info-copy" style={{ marginTop: 8 }}>
                  Your <strong style={{ textTransform: "capitalize" }}>{subscribedPlan}</strong> plan stays active until <strong>{scheduledPlanEndLabel || billingPeriodEndLabel || "the end of this billing period"}</strong>. You can continue using your current plan and AI allowance until that Stripe billing date.
                </div>
              ) : (
                <div className="soft-info-copy" style={{ marginTop: 8 }}>
                  If you downgrade, you will keep your current <strong style={{ textTransform: "capitalize" }}>{subscribedPlan}</strong> access until the Stripe billing period ends.
                </div>
              )}
            </div>
          )}
          {hasScheduledPlanEnd && (
            <div className="soft-info-panel mt-16">
              <div className="soft-info-title">Downgrade scheduled</div>
              <div className="soft-info-copy">
                Your <strong style={{ textTransform: "capitalize" }}>{subscribedPlan}</strong> plan stays active until <strong>{scheduledPlanEndLabel || billingPeriodEndLabel || "the end of this billing period"}</strong>. HouseMind will move this account to Free after Stripe closes that billing cycle.
              </div>
              <div className="soft-info-copy" style={{ marginTop: 8 }}>
                AI usage and paid plan access continue until that Stripe billing date, not by calendar month.
              </div>
            </div>
          )}
          {subscribedPlan === "family" && !billingStatus?.can_manage_billing && (
            <p style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 12 }}>
              Billing for this Family plan can only be managed by {billingStatus?.billing_owner_name || "the current billing owner"}.
            </p>
          )}
          {hasScheduledPlanEnd && (
            <p style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 12 }}>
              Plan changes are locked while this paid subscription is winding down. After {scheduledPlanEndLabel || "the period-end date"}, you can choose a new plan here or use Manage billing in Stripe.
            </p>
          )}
          {billingWorkspace && billingWorkspace.id !== workspaceState?.workspace?.id && subscribedPlan !== "free" && subscribedPlan !== "family" && (
            <p style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 12 }}>
              Your active workspace is <strong>{workspaceState?.workspace?.name || "Personal workspace"}</strong>, but this subscription belongs to <strong>{billingWorkspace.name}</strong>.
            </p>
          )}
        </div>

        <div id="connectors" className="card mb-16">
          <h3 className="section-card-title">📧 Email &amp; calendar connectors</h3>
          <p className="section-card-copy">Connect an inbox or calendar and HouseMind will scan it nightly. High-confidence bills from utility services you already saved update automatically; new or ambiguous findings stay in review.</p>
          <DiscoveryConnectors
            sources={discoverySources}
            onConnect={async (source) => {
              try {
                const updated = await importCandidatesApi.requestConnection(source.id, {});
                if (updated?.oauth?.ready && updated.oauth.auth_url) {
                  window.location.assign(updated.oauth.auth_url);
                } else {
                  setWorkspaceNotice({ tone: "warn", message: updated?.oauth?.message || "Connector credentials are not yet configured on the server. Add GOOGLE_OAUTH_CLIENT_ID / MICROSOFT_OAUTH_CLIENT_ID to your Render environment." });
                }
              } catch (err) {
                setWorkspaceNotice({ tone: "error", message: err?.message || "Could not start connector setup." });
              }
            }}
            onDisconnect={async (source) => {
              try {
                await importCandidatesApi.disconnectConnection(source.id);
                const payload = await importCandidatesApi.sources();
                setDiscoverySourcesPayload(payload);
                setWorkspaceNotice({ tone: "success", message: `${source.label} disconnected.` });
              } catch (err) {
                setWorkspaceNotice({ tone: "error", message: err?.message || "Could not disconnect." });
              }
            }}
          />
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 12 }} onClick={() => navigate("/app?review=sources")}>
            All sources &amp; sync history
          </button>
        </div>

        <div className="card mb-16">
          <h3 className="section-card-title">👥 Family accounts</h3>
          <p className="section-card-copy">Family gives up to 3 private HouseMind accounts under one subscription. Each member keeps their own household data private by default. AI usage comes from one shared Family pool.</p>

        {workspaceNotice && (
          <div className={`notice-banner ${workspaceNotice.tone === "success" ? "is-success" : workspaceNotice.tone === "warn" ? "is-warn" : "is-error"} mb-16`}>
            <p>{workspaceNotice.message}</p>
          </div>
        )}

          <div className="soft-info-panel" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {activeWorkspacePlan === "family" ? "Family plan" : "Private account"}
            </div>
            <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 4 }}>
              {activeWorkspacePlan === "family"
                ? `${familyWorkspace?.member_count || workspaceState?.members?.length || 1} of 3 accounts on the Family plan`
                : `1 private account on the ${activeWorkspacePlan} plan`}
            </div>
            {activeWorkspacePlan === "family" && (
              <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 4 }}>
                Private by default. Family membership does not expose food, people, trips, records, vehicles, subscriptions, or utilities from another member's account.
              </div>
            )}
            <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 4 }}>
              Your role: <strong style={{ textTransform: "capitalize" }}>{workspaceState?.role || "owner"}</strong>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            {(workspaceState?.members || []).map(member => (
              <div key={member.membership_id} className="workspace-member-row" style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                alignItems: "center",
                padding: "10px 12px",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-sm)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {member.profile_picture || member.profile_picture_url || member.avatar_url
                    ? <img src={member.profile_picture || member.profile_picture_url || member.avatar_url} alt={member.full_name} style={{ width: 34, height: 34, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                    : <div style={{ width: 34, height: 34, borderRadius: 8, background: member.avatar_color || "#2A7B6F", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                        {(member.full_name || member.email || "?")[0].toUpperCase()}
                      </div>
                  }
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{member.full_name}</div>
                    <div style={{ fontSize: 12, color: "var(--ink-hint)" }}>{member.email} · {member.role}</div>
                  </div>
                </div>
                {canManageFamilyMembers && member.role !== "owner" ? (
                  <div className="workspace-member-actions" style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <select
                      className="form-select"
                      style={{ width: 110 }}
                      value={member.role}
                      disabled={workspaceLoading}
                      onChange={(e) => handleRoleChange(member.membership_id, e.target.value)}
                    >
                      <option value="member">member</option>
                      <option value="admin">admin</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      style={{ color: "var(--rose)" }}
                      onClick={() => setConfirmAction({
                        title: "Remove member",
                        message: `Remove ${member.full_name} from this Family plan?`,
                        warning: "They will lose access to the Family subscription, shared AI pool, and any active shares with this Family plan. Their private account content stays safe and untouched.",
                        confirmLabel: "Remove member",
                        destructive: true,
                        onConfirm: () => performRemoveMember(member.membership_id),
                      })}
                      disabled={workspaceLoading}
                    >
                      Remove
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>


          {canManageFamilyMembers && duplicatePeopleCleanup?.removable_count > 0 && (
            <div className="soft-info-panel mt-16">
              <div className="soft-info-title">Repair older duplicate people</div>
              <div className="soft-info-copy">
                We found {duplicatePeopleCleanup.matching_count} owner-created people that appear in both your private account and an older Family shared space. This repair only removes stale copies that are not already tied to records or AI suggestions.
              </div>
              <div className="soft-info-copy" style={{ marginTop: 8 }}>
                Ready to remove: {duplicatePeopleCleanup.removable_names.join(", ")}.
              </div>
              {duplicatePeopleCleanup.skippable_count > 0 && (
                <div className="soft-info-copy" style={{ marginTop: 8 }}>
                  {duplicatePeopleCleanup.skippable_count} matching {duplicatePeopleCleanup.skippable_count === 1 ? "person is" : "people are"} already in use and will be left alone.
                </div>
              )}
              {duplicatePeopleCleanup.skippable_names?.length > 0 && (
                <div className="soft-info-copy" style={{ marginTop: 8 }}>
                  Kept in place: {duplicatePeopleCleanup.skippable_names.join(", ")}.
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={workspaceLoading}
                  onClick={() => setConfirmAction({
                    title: "Repair duplicate people",
                    message: `Remove ${duplicatePeopleCleanup.removable_count} stale duplicate ${duplicatePeopleCleanup.removable_count === 1 ? "person" : "people"}?`,
                    warning: "This only removes owner-created copies that already exist in your private account and are not tied to active records or suggestions.",
                    confirmLabel: "Repair duplicates",
                    destructive: true,
                    onConfirm: handleRepairDuplicatePeople,
                  })}
                >
                  Repair duplicate people
                </button>
              </div>
            </div>
          )}


          {workspaceState?.can_manage_members && activeWorkspacePlan !== "family" && (
            <div className="soft-info-panel mt-16">
              <div className="soft-info-title">Extra accounts require Family</div>
              <div className="soft-info-copy">
                Upgrade to Family to add up to 3 private accounts under one subscription.
              </div>
            </div>
          )}

          {currentWorkspaceType === "shared" && currentWorkspaceRole !== "owner" && user?.workspaces?.length > 1 && (
            <div className="mt-16">
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: "var(--rose)" }}
                disabled={workspaceLoading}
                onClick={() => setConfirmAction({
                  title: "Leave Family plan",
                  message: "Leave this Family plan and keep using your private account?",
                  warning: "You will lose access to the Family subscription, shared AI pool, and shared items from this Family plan. Your private account content stays safe and untouched.",
                  confirmLabel: "Leave Family",
                  destructive: true,
                  onConfirm: handleLeaveWorkspace,
                })}
              >
                Leave Family plan
              </button>
            </div>
          )}

          {isStaleSharedWorkspaceOwner && user?.workspaces?.length > 1 && (
            <div className="mt-16">
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: "var(--rose)" }}
                disabled={workspaceLoading}
                onClick={() => setConfirmAction({
                  title: "Leave old Family space",
                  message: "Leave this old Family space and switch back to your private account?",
                  warning: "This space is no longer on the Family plan. Leaving it will remove the old space from your account; your private content stays separate.",
                  confirmLabel: "Leave old space",
                  destructive: true,
                  onConfirm: handleLeaveWorkspace,
                })}
              >
                Leave old Family space
              </button>
            </div>
          )}
        </div>

        <div className="card mb-16">
          <h3 className="section-card-title">🔒 Privacy & account</h3>
          <p className="section-card-copy">Delete your account if you no longer want HouseMind to keep your personal planning data.</p>
          <div className="soft-info-panel">
            <div className="soft-info-title">Delete account</div>
            <div className="soft-info-copy">
              {hasActivePaidSubscription
                ? hasScheduledPlanEnd
                  ? `Account deletion stays locked until your paid subscription finishes on ${scheduledPlanEndLabel || "the Stripe billing end date"}.`
                  : "Cancel your active subscription first. Account deletion stays locked while Stripe billing is still active."
                : "This removes your login and personal data."}
            </div>
            {hasActivePaidSubscription && (
              <div className="soft-info-copy" style={{ marginTop: 8 }}>
                {hasScheduledPlanEnd
                  ? `Your cancellation is already scheduled. Come back after ${scheduledPlanEndLabel || "the billing period ends"} once the plan has switched to Free.`
                  : "Use the Cancel subscription button in the Billing section above, then come back here once your plan shows Free."}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ color: "var(--rose)" }}
                disabled={saving || hasActivePaidSubscription}
                onClick={() => setConfirmAction({
                  title: "Delete account",
                  message: hasActivePaidSubscription
                    ? "Cancel your subscription before deleting this account."
                    : "Delete your HouseMind account and remove your personal data from this app?",
                  warning: hasActivePaidSubscription
                    ? "Go to the Billing section above and cancel the Stripe subscription first."
                    : "This action cannot be undone. You will be signed out immediately after your account is deleted.",
                  confirmLabel: "Delete account",
                  destructive: true,
                  onConfirm: handleDeleteAccount,
                })}
              >
                Delete account
              </button>
            </div>
          </div>
        </div>

        <div id="ai-usage" className="card mb-16" style={{ scrollMarginTop: 24 }}>
          <h3 className="section-card-title">✦ AI usage</h3>
          <p className="section-card-copy">
            {activeWorkspacePlan === "family"
              ? "Your Family plan includes one shared monthly AI pool for all member accounts. Private content stays private."
              : "Your plan includes a monthly AI request allowance for gift ideas, meal planning, household explanations, and reminders."}
          </p>
          <div className="settings-usage-card" style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            padding: "12px 14px",
            borderRadius: "var(--radius-sm)",
            background: remaining === 0 ? "var(--rose-light)" : "var(--cream)",
            border: "1px solid var(--border)",
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                {activeWorkspacePlan === "family" ? "Family AI pool: " : ""}{remaining} of {limit} requests remaining
              </div>
              <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>
                Resets {user?.reset_date ? new Date(`${user.reset_date}T00:00:00`).toLocaleDateString() : "next month"}
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>
              Used: {used}
            </div>
          </div>
          <div className="soft-info-panel mt-16">
            <div className="soft-info-title">Household intelligence</div>
            <div className="soft-info-copy">
              Scan your latest module data now to refresh cross-module insights. The nightly Render job still runs automatically around 2am.
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 12 }}
              disabled={intelligenceRunning}
              onClick={handleRefreshIntelligence}
            >
              {intelligenceRunning
                ? <><span className="spinner" style={{ borderColor: "rgba(28,25,23,0.2)", borderTopColor: "var(--ink)" }} /> Scanning...</>
                : "Refresh household intelligence"}
            </button>
          </div>
        </div>

        {/* AI provider */}
        <div className="card mb-16">
          <h3 className="section-card-title">🎨 Appearance</h3>
          <p className="section-card-copy">Choose how HouseMind should look on this device. System follows your device theme automatically.</p>
          <div className="form-section-stack">
            {THEME_OPTIONS.map(opt => (
              <label key={opt.value} style={{
                display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer",
                padding: "12px 14px", border: "1px solid",
                borderColor: form.theme_preference === opt.value ? "var(--rose)" : "var(--border)",
                borderRadius: "var(--radius-sm)",
                background: form.theme_preference === opt.value ? "var(--rose-light)" : "var(--warm-white)",
                transition: "all 0.15s",
              }}>
                <input
                  type="radio"
                  name="theme_preference"
                  value={opt.value}
                  checked={form.theme_preference === opt.value}
                  onChange={() => {
                    setForm(f => ({ ...f, theme_preference: opt.value }));
                    setThemePreference(opt.value);
                    showOverlay("Applying theme…");
                  }}
                  style={{ marginTop: 3 }}
                />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="card mb-16">
          <h3 className="section-card-title">🌍 Shopping region</h3>
          <p className="section-card-copy">
            Choose where you usually shop so HouseMind can show the most relevant store shortcuts and exact-item links for you.
          </p>
          {!form.shopping_region && (
            <div className="notice-banner mb-16" style={{ background: "var(--gold-light)", borderColor: "rgba(201,168,76,0.45)" }}>
              <p>
                Set this before you rely on store buttons in gift ideas, pantry lists, and household records. If you skip it, HouseMind will fall back to a generic default until you choose a region.
              </p>
            </div>
          )}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Preferred shopping region</label>
            <select
              className="form-select"
              value={form.shopping_region}
              onChange={e => handleShoppingRegionChange(e.target.value)}
              disabled={regionSaving}
            >
              <option value="">Choose your region</option>
              {SHOPPING_REGION_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="text-sm text-muted mt-8">
              You can change this any time. It only affects which shopping shortcuts HouseMind shows first.
            </div>
          </div>
        </div>

        <div className="card mb-16">
          <h3 className="section-card-title">🤖 AI provider</h3>
          <p className="section-card-copy">Choose which model handles your household planning, gift suggestions, and card messages.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {AI_OPTIONS.map(opt => (
              <label key={opt.value} style={{
                display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer",
                padding: "12px 14px", border: "1px solid",
                borderColor: form.ai_provider === opt.value ? "var(--rose)" : "var(--border)",
                borderRadius: "var(--radius-sm)",
                background: form.ai_provider === opt.value ? "var(--rose-light)" : "var(--warm-white)",
                transition: "all 0.15s",
              }}>
                <input type="radio" name="ai_provider" value={opt.value}
                  checked={form.ai_provider === opt.value}
                  onChange={() => handleProviderChange(opt.value)}
                  style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{opt.label}</div>
                  <div style={{ fontSize: 12, color: "var(--ink-hint)", marginTop: 2 }}>{opt.sub}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Reminders */}
        <div className="card mb-24">
          <h3 className="section-card-title">🔔 Email reminders</h3>
          <p className="section-card-copy">
            When to send your first reminder before an occasion.
            You'll also get reminders at 14, 7, 3, and 1 days before regardless.
          </p>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">First reminder</label>
            <select className="form-select" value={form.reminder_days_before}
              onChange={e => setForm(f => ({ ...f, reminder_days_before: parseInt(e.target.value) }))}>
              {REMINDER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex-between gap-8 settings-save-row" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "var(--ink-hint)" }}>
            Current plan: <strong style={{ textTransform: "capitalize" }}>{activeWorkspacePlan}</strong>
          </div>
          <button type="button" className="btn btn-primary" disabled={preferenceSaving} onClick={handlePreferenceSave}>
            {preferenceSaving ? <><span className="spinner" /> Saving…</> : "Save preferences"}
          </button>
        </div>
      </div>

      <div id="guide" className="card mt-24" style={{ scrollMarginTop: 24 }}>
        <h3 className="section-card-title">📘 HouseMind guide</h3>
        <p className="section-card-copy">
          Learn how Food, Pets, Property Profile, Home Care, Emergency &amp; Safety, Household Budget,
          Timeline, and the rest of HouseMind work together. The guide also covers Family sharing,
          Ask HouseMind, plans, and common questions.
        </p>
        <button type="button" className="btn btn-secondary" onClick={() => navigate("/app/guide")}>Open user guide</button>
      </div>

      <div id="support" className="card mt-24" style={{ scrollMarginTop: 24 }}>
        <h3 className="section-card-title">🆘 Support</h3>
        <p className="section-card-copy">
          Reach HouseMind support at <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: "var(--rose)" }}>{SUPPORT_EMAIL}</a> or send a message here. We use your account email so replies go to the right place.
        </p>
        {supportNotice && (
          <div className={`notice-banner ${supportNotice.tone === "success" ? "is-success" : "is-error"} mb-16`}>
            <p>{supportNotice.message}</p>
          </div>
        )}
        <form onSubmit={handleSupportSubmit}>
          <div className="form-group">
            <label className="form-label">What do you need help with?</label>
            <select
              className="form-select"
              value={supportForm.category}
              onChange={e => setSupportForm(current => ({ ...current, category: e.target.value }))}
            >
              {SUPPORT_CATEGORIES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Subject</label>
            <input
              className="form-input"
              value={supportForm.subject}
              onChange={e => setSupportForm(current => ({ ...current, subject: e.target.value }))}
              placeholder="Billing question, bug report, feature request..."
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea
              className="form-textarea"
              rows={4}
              value={supportForm.message}
              onChange={e => setSupportForm(current => ({ ...current, message: e.target.value }))}
              placeholder="Tell us what’s going on and we’ll get back to you."
              required
            />
          </div>
          <div className="flex-between" style={{ alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: "var(--ink-hint)" }}>
              Website: <a href={SITE_URL} target="_blank" rel="noreferrer" style={{ color: "var(--ink-hint)" }}>{SITE_URL}</a>
            </div>
            <button type="submit" className="btn btn-secondary" disabled={supportSending}>
              {supportSending
                ? <><span className="spinner" style={{ borderColor: "rgba(28,25,23,0.2)", borderTopColor: "var(--ink)" }} /> Sending…</>
                : "Send to support"}
            </button>
          </div>
        </form>
      </div>

      {showSettingsNotice && (
        <SavingNotice title={settingsNoticeTitle || "Saving"} message={settingsNoticeMessage || "Working..."} />
      )}
    </div>
  );
}
